import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Dashboard "Inspired by you" rail.
 *
 * Quietly polls /clips/inspired-by-athlete/{id}; renders nothing when
 * the athlete hasn't uploaded anything in the last 48 hours. This is
 * the dashboard lift of the same loop that runs on VideoUploadPage —
 * the goal is to keep "what good looks like" front-of-mind right
 * after the kid practices.
 */
export default function InspiredByYouCard({ athleteId, theme }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!athleteId || !token) return;
    axios.get(`${API}/clips/inspired-by-athlete/${athleteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data?.inspired || null))
      .catch(() => setData(null));
  }, [athleteId, token]);

  if (!data || !data.clips?.length) return null;

  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.10), rgba(0,217,255,0.08))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div
            className="text-[10px] uppercase tracking-widest font-bold"
            style={{ color: '#00C9A7' }}
          >
            ✨ Inspired by your last session
          </div>
          <h2 className="font-bold mt-0.5">Watch how the pros do it</h2>
        </div>
        {data.technique && (
          <span
            className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded shrink-0"
            style={{ background: 'rgba(0,201,167,0.18)', color: '#00C9A7' }}
          >
            {data.technique.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {data.clips.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/clips?focus=${c.id}`)}
            className="text-left rounded-xl overflow-hidden transition-transform hover:-translate-y-0.5"
            style={{
              background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(150,160,180,0.2)',
            }}
          >
            <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000' }}>
              {c.thumbnail_url && (
                <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
              )}
              <span
                className="absolute bottom-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}
              >
                {c.duration_seconds || 30}s
              </span>
            </div>
            <div className="p-2.5">
              <div className="text-xs font-semibold leading-tight line-clamp-2">
                {c.title}
              </div>
              {c.player?.name && (
                <div className="text-[11px] opacity-60 mt-0.5 truncate">
                  {c.player.name}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
